import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Typography,
  Tooltip,
} from '@mui/material';
import React, { FC } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useGetUseCasesApi, EnumOption, UseCaseOption, UseCasesResponse } from 'src/api/campaignRegistry';

// Enhanced interface that supports both react-hook-form and simple value/onChange
export interface SubUseCaseSelectorProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form?: UseFormReturn<any>;
  useCaseField?: string;
  subUseCasesField?: string;
  disabled?: boolean;
  useCasesData?: UseCasesResponse;
  // Alternative to form-based approach
  useCaseValue?: string;
  subUseCasesValue?: string[];
  onUseCaseChange?: (value: string) => void;
  onSubUseCasesChange?: (value: string[]) => void;
}

export const SubUseCaseSelector: FC<SubUseCaseSelectorProps> = ({
  form,
  useCaseField = 'useCase',
  subUseCasesField = 'subUseCases',
  disabled = false,
  useCasesData: externalUseCasesData,
  useCaseValue,
  subUseCasesValue,
  onSubUseCasesChange,
}) => {

  const { watch, setValue } = form || {};

  // Support both form-based and value/onChange approaches
  const useCase = form ? watch?.(useCaseField) : useCaseValue;

  const [useCasesData, setUseCasesData] = React.useState<UseCasesResponse>({});
  const [loadingUseCases, setLoadingUseCases] = React.useState(false);

  const getUseCases = useGetUseCasesApi();

  // Fetch use cases on component mount or use external data
  React.useEffect(() => {
    const fetchUseCases = async () => {
      try {
        setLoadingUseCases(true);
        const response = await getUseCases();
        if (response.data && Object.keys(response.data).length > 0) {
          setUseCasesData(response.data);
        } else {
          setUseCasesData({});
        }
      } catch (error) {
        console.error('Failed to fetch use cases:', error);
        setUseCasesData({});
      } finally {
        setLoadingUseCases(false);
      }
    };

    if (externalUseCasesData && Object.keys(externalUseCasesData).length > 0) {
      // Use external data if provided
      setUseCasesData(externalUseCasesData);
      setLoadingUseCases(false);
    } else {
      // Fetch data if not provided externally
      fetchUseCases();
    }
  }, [getUseCases, externalUseCasesData]);

  const getSubUseCasesOptions = (): EnumOption[] => {
    if (useCasesData && Object.keys(useCasesData).length > 0) {
      return Object.entries(useCasesData)
        .filter(([, useCase]) => useCase.validSubUsecase === true)
        .map(([key, useCase]) => ({
          value: key,
          label: useCase.displayName,
        }));
    }

    return [];
  };

  const getUseCaseDetails = (useCaseKey: string): UseCaseOption | null => {
    if (useCasesData[useCaseKey]) {
      return useCasesData[useCaseKey];
    }

    // Try with default_ prefix
    const withPrefix = `default_${useCaseKey}`;
    if (useCasesData[withPrefix]) {
      return useCasesData[withPrefix];
    }

    // Try without default_ prefix
    const withoutPrefix = useCaseKey.replace(/^default_/, '');
    if (useCasesData[withoutPrefix]) {
      return useCasesData[withoutPrefix];
    }

    return null;
  };

  const shouldShowSubUsecases = (): boolean => {
    const currentUseCase = form ? useCase : useCaseValue;

    if (!currentUseCase) return false;
    const useCaseDetails = getUseCaseDetails(currentUseCase as string);
    // Show sub-use cases only if the current use case supports them
    return (useCaseDetails?.maxSubUsecases && useCaseDetails.maxSubUsecases > 0) || false;
  };

  // Clear sub-use cases when switching to a use case that doesn't support them
  // For form-based approach - only when use case changes (not on initial load)
  React.useEffect(() => {
    if (form && useCase && Object.keys(useCasesData).length > 0) {
      const currentSubUseCases = watch?.(subUseCasesField) || [];
      if (Array.isArray(currentSubUseCases) && currentSubUseCases.length > 0) {
        const useCaseDetails = getUseCaseDetails(useCase as string);
        const supportsSubUseCases = (useCaseDetails?.maxSubUsecases && useCaseDetails.maxSubUsecases > 0) || false;

        if (!supportsSubUseCases && setValue) {
          setValue(subUseCasesField, []);
        }
      }
    }
  }, [form, useCase, watch, subUseCasesField, setValue, useCasesData]);

  // For value/onChange approach
  React.useEffect(() => {
    // Only clear sub-use cases if use cases data is loaded and use case doesn't support them
    if (!form && useCaseValue && Array.isArray(subUseCasesValue) && subUseCasesValue.length > 0 && Object.keys(useCasesData).length > 0) {
      const useCaseDetails = getUseCaseDetails(useCaseValue as string);
      const supportsSubUseCases = (useCaseDetails?.maxSubUsecases && useCaseDetails.maxSubUsecases > 0) || false;

      if (!supportsSubUseCases && onSubUseCasesChange) {
        onSubUseCasesChange([]);
      }
    }
  }, [form, useCaseValue, subUseCasesValue, onSubUseCasesChange, useCasesData]);

  if (!shouldShowSubUsecases()) {
    return null;
  }
  return (
    <>
      <Typography
        variant="h6"
        sx={{ marginBottom: '0px', fontSize: '1rem', color: 'text.secondary' }}
      >
        Sub-Use Cases ({Array.isArray(form ? watch?.(subUseCasesField) : subUseCasesValue) ? (form ? watch?.(subUseCasesField) : subUseCasesValue)?.length : 0}/
        {getUseCaseDetails((form ? useCase : useCaseValue) as string)?.maxSubUsecases || 5})
      </Typography>

      <FormControl fullWidth>
        <FormGroup>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 0.5,
              alignItems: 'center',
              padding: 0,
            }}
          >
            {(() => {
              // Get current sub-use cases for checking state
              const currentSubUseCases = (form ? watch?.(subUseCasesField) : subUseCasesValue) || [];

              // Get all predefined subUsecases
              const predefinedSubUseCases = getSubUseCasesOptions();

              // Add any existing subUsecases that aren't in the predefined list
              const existingSubUsecases = currentSubUseCases as string[];
              const additionalSubUseCases = existingSubUsecases
                .filter(
                  sub => !predefinedSubUseCases.some((p: EnumOption) => p.value === sub),
                )
                .map(sub => ({ value: sub, label: sub }));

              return [...predefinedSubUseCases, ...additionalSubUseCases];
            })().map(subUseCase => {
              const subUseCaseDetails = getUseCaseDetails(subUseCase.value);
              const currentSubUseCases = (form ? watch?.(subUseCasesField) : subUseCasesValue) || [];

              // Handle both formats: with and without "default_" prefix
              const isChecked = Array.isArray(currentSubUseCases) && (
                currentSubUseCases.includes(subUseCase.value) ||
                currentSubUseCases.includes(`default_${subUseCase.value}`) ||
                currentSubUseCases.includes(subUseCase.value.replace(/^default_/, ''))
              );

              return (
                <Tooltip
                  key={subUseCase.value}
                  title={subUseCaseDetails?.description || ''}
                  placement="top"
                  arrow
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        disabled={disabled || loadingUseCases}
                        onChange={e => {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const freshSubUsecases = Array.isArray(currentSubUseCases) ? currentSubUseCases : [];
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const useCaseDetails = getUseCaseDetails((form ? useCase : useCaseValue) as any);
                          const maxAllowed = useCaseDetails?.maxSubUsecases || 5;

                          let newSubUseCases: string[];
                          if (e.target.checked) {
                            if (freshSubUsecases.length < maxAllowed) {
                              // Add the value in the same format as existing values
                              const valueToAdd = freshSubUsecases.length > 0 && freshSubUsecases[0].startsWith('default_')
                                ? `default_${subUseCase.value}`
                                : subUseCase.value;
                              newSubUseCases = [
                                ...freshSubUsecases,
                                valueToAdd,
                              ];
                            } else {
                              return; // Don't add if at max
                            }
                          } else {
                            // Remove both formats
                            newSubUseCases = freshSubUsecases.filter((s: string) =>
                              s !== subUseCase.value &&
                              s !== `default_${subUseCase.value}` &&
                              s !== subUseCase.value.replace(/^default_/, '')
                            );
                          }

                          if (form && setValue) {
                            setValue(subUseCasesField, newSubUseCases);
                          } else if (onSubUseCasesChange) {
                            onSubUseCasesChange(newSubUseCases);
                          }
                        }}
                        sx={{
                          '&.Mui-disabled': {
                            opacity: 0.6,
                          },
                        }}
                      />
                    }
                    label={subUseCase.label}
                    sx={{
                      margin: 0,
                      width: '100%',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                        marginLeft: 0.5,
                      },
                      '& .MuiCheckbox-root': {
                        padding: '6px',
                      },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </FormGroup>
      </FormControl>
    </>
  );
};
